<template>
  <v-tooltip left>
    <template #activator="{ on, attrs }">
      <v-btn
        color="white"
        class="assign-student-task-btn"
        :class="{ 'assign-student-task-btn--created': hasCreatedTaskHere }"
        icon
        :loading="loading.task || loading.existing"
        :disabled="buttonDisabled"
        :style="buttonStyle"
        :aria-label="
          hasCreatedTaskHere ? 'Homework already assigned for this step' : 'Assign student homework'
        "
        v-bind="attrs"
        v-on="on"
        @click="onAssignStudentTask"
      >
        <v-icon color="white">{{
          hasCreatedTaskHere ? "mdi-check" : "mdi-clipboard-text-outline"
        }}</v-icon>
      </v-btn>
    </template>
    <span>{{ assignTooltip }}</span>
  </v-tooltip>
</template>

<script>
import { createStudentTask, listStudentTasks } from "@/api/api";
import studentMixin from "@/mixins/studentMixin";
import lessonStepContextMixin from "@/mixins/lessonStepContextMixin";

const TASK_LIST_PAGE_SIZE = 100;

function stepCreationKey({ studentEmail, lessonUuid, stepUuid, stepType, stepNumber }) {
  const parts = [
    studentEmail ?? "",
    lessonUuid ?? "",
    String(stepUuid ?? ""),
    stepType ?? "",
    String(stepNumber ?? ""),
  ];
  return parts.join("\u0000");
}

export default {
  name: "AssignStudentTaskButton",
  mixins: [studentMixin, lessonStepContextMixin],
  data() {
    return {
      loading: {
        task: false,
        existing: false,
      },
      existingStepKeys: Object.create(null),
    };
  },
  computed: {
    creationKey() {
      return stepCreationKey({
        studentEmail: this.student?.email,
        lessonUuid: this.currentLesson?.uuid,
        stepUuid: this.stepUuid,
        stepType: this.stepType,
        stepNumber: this.stepNumber,
      });
    },
    contextReady() {
      return Boolean(
        this.student?.email && this.currentLesson?.uuid && this.stepUuid && this.stepType,
      );
    },
    hasCreatedTaskHere() {
      return Boolean(this.existingStepKeys[this.creationKey]);
    },
    buttonDisabled() {
      return (
        this.loading.task || this.loading.existing || !this.contextReady || this.hasCreatedTaskHere
      );
    },
    buttonStyle() {
      if (this.hasCreatedTaskHere) {
        return { backgroundColor: "#FF347C" };
      }
      return { backgroundColor: "#29284c" };
    },
    assignTooltip() {
      if (this.hasCreatedTaskHere) {
        return "Homework already assigned for this step.";
      }
      if (!this.student?.email) {
        return "Select a student to assign a homework";
      }
      if (!this.currentLesson?.uuid) {
        return "Open a lesson to assign a homework";
      }
      if (!this.stepUuid || !this.stepType) {
        return "This step cannot be assigned yet";
      }
      return "Assign as homework";
    },
  },
  watch: {
    "student.email"(email) {
      this.existingStepKeys = Object.create(null);
      if (email) {
        this.fetchExistingTasksForStudent();
      }
    },
  },
  mounted() {
    if (this.student?.email) {
      this.fetchExistingTasksForStudent();
    }
  },
  methods: {
    taskToCreationKey(task) {
      return stepCreationKey({
        studentEmail: task.student_email,
        lessonUuid: task.lesson_uuid,
        stepUuid: task.step_uuid,
        stepType: task.step_type,
        stepNumber: task.step_number,
      });
    },
    async fetchExistingTasksForStudent() {
      const email = this.student?.email;
      if (!email) {
        this.existingStepKeys = Object.create(null);
        return;
      }

      this.loading.existing = true;
      try {
        const keys = Object.create(null);
        let offset = 0;
        let hasMore = true;

        while (hasMore) {
          const { data } = await listStudentTasks({
            student__email: email,
            limit: TASK_LIST_PAGE_SIZE,
            offset,
          });

          for (const task of data.results || []) {
            if (task.status === "COMPLETED") {
              continue;
            }
            keys[this.taskToCreationKey(task)] = true;
          }

          if (data.next) {
            offset += TASK_LIST_PAGE_SIZE;
          } else {
            hasMore = false;
          }
        }

        this.existingStepKeys = keys;
      } catch {
        this.existingStepKeys = Object.create(null);
      } finally {
        this.loading.existing = false;
      }
    },
    formatTaskApiError(error) {
      const d = error.response?.data;
      if (!d || typeof d !== "object") {
        return error.message || "Unknown error";
      }
      const entries = Object.entries(d);
      if (!entries.length) {
        return "Unknown error";
      }
      const [key, value] = entries[0];
      return Array.isArray(value) ? `${key}: ${value[0]}` : `${key}: ${value}`;
    },
    async onAssignStudentTask() {
      if (this.buttonDisabled || this.hasCreatedTaskHere) {
        return;
      }
      this.loading.task = true;
      try {
        await createStudentTask({
          student: this.student.email,
          lesson: this.currentLesson.uuid,
          step_uuid: this.stepUuid,
          step_type: this.stepType,
          step_number: this.stepNumber,
        });
        this.$set(this.existingStepKeys, this.creationKey, true);
      } catch (error) {
        this.$store.commit("updateSnackbar", {
          statusText: "Could not assign homework",
          message: this.formatTaskApiError(error),
        });
      } finally {
        this.loading.task = false;
      }
    },
  },
};
</script>

<style scoped>
.assign-student-task-btn {
  position: fixed;
  right: 16px;
  bottom: 88px;
  z-index: 1000;
  color: white;
}

.assign-student-task-btn.v-btn.v-btn--disabled:not(.v-btn--loading) {
  opacity: 1 !important;
  background-color: #29284c !important;
}

.assign-student-task-btn.v-btn.v-btn--disabled:not(.v-btn--loading) >>> .v-icon {
  color: #fff !important;
  opacity: 1 !important;
}

.assign-student-task-btn--created.v-btn.v-btn--disabled:not(.v-btn--loading) {
  background-color: #ff347c !important;
}
</style>
